import { useEffect, useState } from "react";
import { useAuthStore, useAppStore, api } from "@/store";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Building2, Users, Plus, Trash2, Shield, MapPin, Key, UserCheck, UserX,
  Wand2, FileSpreadsheet, Save, Grid3X3, Pencil, AlertTriangle, CheckCircle2,
  ArrowRightLeft, Activity, Ban, RefreshCw, RotateCcw, Archive, PackageOpen,
  ShieldCheck, ShieldAlert, Loader2, Presentation
} from "lucide-react";
import { toast } from "sonner";
import { t } from "@/i18n/bg";
import VisualInputMapEditor from "@/components/VisualInputMapEditor";

export default function AdminPage() {
  const isAdmin = useAuthStore((s) => s.isAdmin);
  const { units, users, templates, fetchUnits, fetchUsers, fetchTemplates } = useAppStore();
  const [showUserDialog, setShowUserDialog] = useState(false);
  const [showUnitDialog, setShowUnitDialog] = useState(false);
  const [showEditUnitDialog, setShowEditUnitDialog] = useState(false);
  const [showBulkDialog, setShowBulkDialog] = useState(false);
  const [showDeactivateDialog, setShowDeactivateDialog] = useState(false);
  const [showReassignDialog, setShowReassignDialog] = useState(false);
  const [showPermanentDeleteDialog, setShowPermanentDeleteDialog] = useState(false);
  const [permanentDeleteTarget, setPermanentDeleteTarget] = useState(null);
  const [permanentDeleteConfirm, setPermanentDeleteConfirm] = useState("");
  const [newUser, setNewUser] = useState({ email: "", password: "", name: "", role: "UNIT_USER", unit_id: "" });
  const [newUnit, setNewUnit] = useState({ name: "", code: "" });
  const [editUnit, setEditUnit] = useState(null);
  const [deactivateTarget, setDeactivateTarget] = useState(null);
  const [reassignTarget, setReassignTarget] = useState(null);
  const [reassignTargetUnit, setReassignTargetUnit] = useState("");
  const [unitsTab, setUnitsTab] = useState("active");
  const [inputMapTemplateId, setInputMapTemplateId] = useState(null);
  const [inputMapJson, setInputMapJson] = useState("");
  const [inputMapError, setInputMapError] = useState("");
  const [formulaScanTemplateId, setFormulaScanTemplateId] = useState(null);
  const [validationReport, setValidationReport] = useState(null);
  const [validating, setValidating] = useState(false);
  // Maintenance mode
  const [maintenanceEnabled, setMaintenanceEnabled] = useState(false);
  const [maintenanceMsg, setMaintenanceMsg] = useState("");
  const [maintenanceSaving, setMaintenanceSaving] = useState(false);
  const [generatingPptx, setGeneratingPptx] = useState(false);

  useEffect(() => {
    if (isAdmin()) {
      fetchUsers();
      fetchTemplates();
      api.get("/settings/maintenance").then(r => {
        setMaintenanceEnabled(r.data.enabled || false);
        setMaintenanceMsg(r.data.message || "");
      }).catch(() => {});
    }
  }, []);

  useEffect(() => {
    if (isAdmin()) {
      if (unitsTab === "active") fetchUnits(false);
      else fetchUnits(false, unitsTab === "archived" ? "archived" : "trashed");
    }
  }, [unitsTab]);

  // ─── Maintenance Mode ───
  const toggleMaintenance = async () => {
    setMaintenanceSaving(true);
    try {
      const newState = !maintenanceEnabled;
      await api.put("/settings/maintenance", { enabled: newState, message: maintenanceMsg });
      setMaintenanceEnabled(newState);
      toast.success(newState ? t("maintenanceEnabled") : t("maintenanceDisabled"));
    } catch (e) { toast.error(e.response?.data?.detail || t("failed")); }
    finally { setMaintenanceSaving(false); }
  };

  // ─── User Handlers ───
  const handleCreateUser = async () => {
    try {
      await api.post("/users", newUser);
      toast.success(t("userCreated")); fetchUsers(); setShowUserDialog(false);
      setNewUser({ email: "", password: "", name: "", role: "UNIT_USER", unit_id: "" });
    } catch (err) { toast.error(err.response?.data?.detail || t("failed")); }
  };
  const handleDeleteUser = async (userId) => {
    if (!window.confirm(t("deleteConfirm"))) return;
    try { await api.delete(`/users/${userId}`); toast.success(t("userDeleted")); fetchUsers(); }
    catch { toast.error(t("failed")); }
  };
  const handleToggleActive = async (userId, currentActive) => {
    try {
      await api.patch(`/users/${userId}`, { active: !currentActive });
      toast.success(currentActive ? t("userDeactivated") : t("userActivated")); fetchUsers();
    } catch { toast.error(t("failed")); }
  };
  const handleResetPassword = async (userId) => {
    const newPw = prompt(t("enterNewPassword"));
    if (!newPw || newPw.length < 6) return;
    try { await api.patch(`/users/${userId}`, { password: newPw }); toast.success(t("passwordReset")); }
    catch { toast.error(t("failed")); }
  };
  const handleReassignUser = async (userId, newUnitId) => {
    try {
      await api.patch(`/users/${userId}`, { unit_id: newUnitId });
      toast.success(t("usersReassigned")); fetchUsers();
    } catch (err) { toast.error(err.response?.data?.detail || t("failed")); }
  };
  const handleBulkProvision = async () => {
    const activeUnitsList = units.filter(u => u.active !== false && (u.deletion_status || "active") === "active");
    const bulkUsers = activeUnitsList.filter(u => !users.some(usr => usr.unit_id === u.id && usr.role === "UNIT_USER")).map(unit => ({
      email: `user@${unit.code.toLowerCase()}.bg`, password: "unit2025",
      name: `${unit.name} User`, role: "UNIT_USER", unit_id: unit.id
    }));
    if (bulkUsers.length === 0) { toast.info("Всички звена вече имат потребители"); return; }
    try {
      const res = await api.post("/users/bulk", { users: bulkUsers });
      toast.success(`Създадени ${res.data.created} потребители`);
      fetchUsers(); setShowBulkDialog(false);
    } catch (err) { toast.error(err.response?.data?.detail || t("failed")); }
  };

  // ─── Unit Handlers ───
  const handleCreateUnit = async () => {
    try {
      await api.post("/units", newUnit);
      toast.success(t("unitCreated")); refreshUnitsTab(); setShowUnitDialog(false); setNewUnit({ name: "", code: "" });
    } catch (err) { toast.error(err.response?.data?.detail || t("failed")); }
  };
  const handleEditUnit = async () => {
    if (!editUnit) return;
    try {
      await api.patch(`/units/${editUnit.id}`, { name: editUnit.name, code: editUnit.code, director: editUnit.director || "", accountant: editUnit.accountant || "" });
      toast.success(t("unitUpdated")); refreshUnitsTab(); setShowEditUnitDialog(false);
    } catch (err) { toast.error(err.response?.data?.detail || t("failed")); }
  };
  const handleDeactivateUnit = async () => {
    if (!deactivateTarget) return;
    try {
      await api.post(`/admin/units/${deactivateTarget.id}/archive`);
      toast.success(t("unitArchived")); refreshUnitsTab(); fetchUsers(); setShowDeactivateDialog(false);
    } catch (err) { toast.error(err.response?.data?.detail || t("failed")); }
  };
  const handleRestoreUnit = async (unitId) => {
    try { await api.post(`/admin/units/${unitId}/restore`); toast.success(t("unitRestored")); refreshUnitsTab(); }
    catch (err) { toast.error(err.response?.data?.detail || t("failed")); }
  };
  const handleTrashUnit = async (unitId) => {
    try { await api.post(`/admin/units/${unitId}/trash`); toast.success(t("unitTrashed")); refreshUnitsTab(); }
    catch (err) { toast.error(err.response?.data?.detail || t("failed")); }
  };
  const handleUntrashUnit = async (unitId) => {
    try { await api.post(`/admin/units/${unitId}/untrash`); toast.success(t("unitUntrashedToArchive")); refreshUnitsTab(); }
    catch (err) { toast.error(err.response?.data?.detail || t("failed")); }
  };
  const handlePermanentDeleteUnit = async () => {
    if (!permanentDeleteTarget || permanentDeleteConfirm !== "DELETE") return;
    try {
      await api.delete(`/admin/units/${permanentDeleteTarget.id}/permanent`, { data: { confirm: "DELETE" } });
      toast.success(t("unitPermanentlyDeleted"));
      setShowPermanentDeleteDialog(false); setPermanentDeleteConfirm(""); setPermanentDeleteTarget(null);
      refreshUnitsTab();
    } catch (err) { toast.error(err.response?.data?.detail || t("failed")); }
  };
  const handleReassignUsers = async () => {
    if (!reassignTarget || !reassignTargetUnit) return;
    try {
      const res = await api.post(`/units/${reassignTarget.id}/reassign-users?target_unit_id=${reassignTargetUnit}`);
      toast.success(`${t("usersReassigned")}: ${res.data.count}`); fetchUsers(); setShowReassignDialog(false);
    } catch (err) { toast.error(err.response?.data?.detail || t("failed")); }
  };

  const refreshUnitsTab = () => {
    if (unitsTab === "active") fetchUnits(false);
    else fetchUnits(false, unitsTab === "archived" ? "archived" : "trashed");
  };

  // ─── Formula Validation ───
  const runFormulaValidation = async (tid) => {
    setValidating(true);
    try {
      const res = await api.post(`/templates/${tid}/validate-formulas`);
      setValidationReport(res.data);
    } catch (err) { toast.error(err.response?.data?.detail || t("failed")); }
    finally { setValidating(false); }
  };

  // ─── Input Map ───
  const loadInputMap = async (tid) => {
    try {
      const res = await api.get(`/templates/${tid}/input-map`);
      setInputMapJson(JSON.stringify(res.data, null, 2));
      setInputMapTemplateId(tid); setInputMapError("");
    } catch { toast.error(t("loadFailed")); }
  };
  const saveInputMap = async () => {
    try {
      const parsed = JSON.parse(inputMapJson);
      const res = await api.put(`/templates/${inputMapTemplateId}/input-map`, parsed);
      toast.success(`${t("inputMapSaved")} ${res.data.editable_count} ${t("editableCells")}`);
      setInputMapError(""); fetchTemplates();
    } catch (e) {
      if (e instanceof SyntaxError) setInputMapError(t("invalidJson"));
      else { setInputMapError(e.response?.data?.detail || t("failed")); toast.error(t("saveFailed")); }
    }
  };

  const handleGeneratePresentation = async () => {
    setGeneratingPptx(true);
    toast.info("Генериране на презентация... Моля изчакайте (до 2 минути).");
    try {
      const res = await api.post("/admin/generate-presentation", {}, { responseType: "blob", timeout: 180000 });
      const a = document.createElement("a");
      a.href = window.URL.createObjectURL(new Blob([res.data]));
      a.download = "SZDP_System_Presentation.pptx";
      a.click();
      toast.success("Презентацията е готова!");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Грешка при генериране на презентация");
    } finally {
      setGeneratingPptx(false);
    }
  };

  if (!isAdmin()) return <div className="flex items-center justify-center h-full"><p className="text-muted-foreground">{t("adminOnly")}</p></div>;

  const activeUnits = units.filter(u => u.active !== false && (u.deletion_status || "active") === "active");

  return (
    <div className="p-6 lg:p-8 space-y-8">
      <div className="flex items-center justify-between">
        <div>
          <h1 data-testid="admin-title" className="text-3xl font-semibold tracking-tight text-[#14532d]">{t("administration")}</h1>
          <p className="text-sm text-muted-foreground mt-1">{t("adminDesc")}</p>
        </div>
        <Button data-testid="generate-presentation" variant="outline" size="sm"
          onClick={handleGeneratePresentation} disabled={generatingPptx}
          className="h-9 text-xs text-[#14532d] border-[#14532d]/30 hover:bg-emerald-50">
          {generatingPptx ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Presentation className="w-4 h-4 mr-2" />}
          {generatingPptx ? "Генериране..." : "Презентация на системата"}
        </Button>
      </div>

      {/* Maintenance Mode Panel */}
      <div className={`rounded-lg border p-4 flex items-center justify-between ${maintenanceEnabled ? "bg-red-50 border-red-300" : "bg-slate-50 border-slate-200"}`}>
        <div className="flex items-center gap-3">
          <AlertTriangle className={`w-5 h-5 ${maintenanceEnabled ? "text-red-600" : "text-slate-400"}`} />
          <div>
            <p className={`text-sm font-semibold ${maintenanceEnabled ? "text-red-800" : "text-slate-700"}`}>{t("maintenanceMode")}</p>
            <p className="text-xs text-slate-500">{t("maintenanceModeDesc")}</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <Input data-testid="maintenance-msg" value={maintenanceMsg}
            onChange={e => setMaintenanceMsg(e.target.value)}
            className="h-8 w-64 text-xs" placeholder={t("maintenanceMessagePlaceholder")} />
          <Button data-testid="toggle-maintenance" size="sm" onClick={toggleMaintenance} disabled={maintenanceSaving}
            className={maintenanceEnabled ? "bg-green-600 hover:bg-green-700 text-white" : "bg-red-600 hover:bg-red-700 text-white"}>
            {maintenanceSaving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : null}
            {maintenanceEnabled ? t("disableMaintenance") : t("enableMaintenance")}
          </Button>
        </div>
      </div>

      <Tabs defaultValue="visual" className="w-full">
        <TabsList className="bg-slate-100 h-10">
          <TabsTrigger data-testid="tab-visual" value="visual" className="text-sm"><Grid3X3 className="w-4 h-4 mr-2" />{t("visualEditorTab")}</TabsTrigger>
          <TabsTrigger data-testid="tab-units" value="units" className="text-sm"><Building2 className="w-4 h-4 mr-2" />{t("unitsTab")} ({units.length})</TabsTrigger>
          <TabsTrigger data-testid="tab-users" value="users" className="text-sm"><Users className="w-4 h-4 mr-2" />{t("usersTab")} ({users.length})</TabsTrigger>
          <TabsTrigger data-testid="tab-formulas" value="formulas" className="text-sm"><Activity className="w-4 h-4 mr-2" />{t("formulaCheck")}</TabsTrigger>
          <TabsTrigger data-testid="tab-inputmap" value="inputmap" className="text-sm"><FileSpreadsheet className="w-4 h-4 mr-2" />{t("inputMapTab")}</TabsTrigger>
        </TabsList>

        <TabsContent value="visual" className="mt-6"><VisualInputMapEditor templates={templates.filter(tp => !tp.is_deleted && (tp.deletion_status || "active") === "active")} /></TabsContent>

        {/* ═══════════ Units Tab ═══════════ */}
        <TabsContent value="units" className="mt-6">
          <Card className="border border-slate-200 shadow-sm">
            <CardHeader className="flex flex-row items-center justify-between py-4 px-6 border-b border-slate-100">
              <CardTitle className="text-base font-semibold text-slate-800">{t("territorialUnits")}</CardTitle>
              <Dialog open={showUnitDialog} onOpenChange={setShowUnitDialog}>
                <DialogTrigger asChild><Button data-testid="add-unit-button" size="sm" className="h-9 bg-[#14532d] hover:bg-[#166534] text-white"><Plus className="w-4 h-4 mr-1" /> {t("addUnit")}</Button></DialogTrigger>
                <DialogContent>
                  <DialogHeader><DialogTitle>{t("addNewUnit")}</DialogTitle></DialogHeader>
                  <div className="space-y-4 pt-4">
                    <div><Label>{t("unitName")}</Label><Input data-testid="unit-name-input" value={newUnit.name} onChange={(e) => setNewUnit({...newUnit, name: e.target.value})} placeholder="ДГС Ловеч" /></div>
                    <div><Label>{t("unitCode")}</Label><Input data-testid="unit-code-input" value={newUnit.code} onChange={(e) => setNewUnit({...newUnit, code: e.target.value})} placeholder="DGS-LOV" /></div>
                    <div><Label>{t("director")}</Label><Input value={newUnit.director || ""} onChange={(e) => setNewUnit({...newUnit, director: e.target.value})} placeholder={t("directorPlaceholder")} /></div>
                    <div><Label>{t("accountant")}</Label><Input value={newUnit.accountant || ""} onChange={(e) => setNewUnit({...newUnit, accountant: e.target.value})} placeholder={t("accountantPlaceholder")} /></div>
                    <Button data-testid="save-unit-button" onClick={handleCreateUnit} className="w-full bg-[#14532d] hover:bg-[#166534] text-white"><Plus className="w-4 h-4 mr-2" /> {t("create")}</Button>
                  </div>
                </DialogContent>
              </Dialog>
            </CardHeader>
            <CardContent className="p-4">
              <Tabs value={unitsTab} onValueChange={setUnitsTab} className="w-full">
                <TabsList className="bg-slate-50 h-9 mb-4">
                  <TabsTrigger data-testid="units-tab-active" value="active" className="text-xs">{t("activeTab")}</TabsTrigger>
                  <TabsTrigger data-testid="units-tab-archived" value="archived" className="text-xs">{t("archivedTab")}</TabsTrigger>
                  <TabsTrigger data-testid="units-tab-trashed" value="trashed" className="text-xs">{t("trashTab")}</TabsTrigger>
                </TabsList>
                {["active", "archived", "trashed"].map(tab => (
                  <TabsContent key={tab} value={tab} className="mt-0">
                    {units.length === 0 ? (
                      <div className="flex flex-col items-center py-12">
                        <PackageOpen className="w-10 h-10 text-slate-300 mb-3" />
                        <p className="text-sm text-slate-500">
                          {tab === "active" ? t("noTemplates") : tab === "archived" ? t("noArchivedUnits") : t("noTrashedUnits")}
                        </p>
                      </div>
                    ) : (
                      <table className="w-full text-sm">
                        <thead><tr className="border-b border-slate-100 bg-slate-50/50">
                          <th className="text-left py-3 px-6 font-medium text-slate-500 text-xs uppercase tracking-wider">{t("name")}</th>
                          <th className="text-left py-3 px-3 font-medium text-slate-500 text-xs uppercase tracking-wider">{t("unitCode")}</th>
                          <th className="text-center py-3 px-3 font-medium text-slate-500 text-xs uppercase tracking-wider">{t("userCount")}</th>
                          <th className="text-center py-3 px-3 font-medium text-slate-500 text-xs uppercase tracking-wider">{t("instanceCount")}</th>
                          <th className="text-right py-3 px-6 font-medium text-slate-500 text-xs uppercase tracking-wider">{t("actions")}</th>
                        </tr></thead>
                        <tbody>{units.map((u) => (
                          <tr key={u.id} data-testid={`unit-row-${u.id}`} className="border-b border-slate-50 hover:bg-slate-50/50">
                            <td className="py-3 px-6 flex items-center gap-2"><MapPin className="w-3.5 h-3.5 text-[#14532d]" /><span className="font-medium text-slate-800">{u.name}</span></td>
                            <td className="py-3 px-3 text-slate-600 font-mono text-xs">{u.code}</td>
                            <td className="py-3 px-3 text-center text-xs">{u.user_count ?? "-"}</td>
                            <td className="py-3 px-3 text-center text-xs">{u.instance_count ?? "-"}</td>
                            <td className="py-3 px-6 text-right">
                              <div className="flex items-center justify-end gap-1">
                                {tab === "active" && (
                                  <>
                                    <Button variant="ghost" size="sm" className="h-7 text-[10px] text-slate-500"
                                      onClick={() => { setEditUnit({...u}); setShowEditUnitDialog(true); }}>
                                      <Pencil className="w-3 h-3" />
                                    </Button>
                                    {u.user_count > 0 && (
                                      <Button variant="ghost" size="sm" className="h-7 text-[10px] text-blue-500"
                                        onClick={() => { setReassignTarget(u); setReassignTargetUnit(""); setShowReassignDialog(true); }}>
                                        <ArrowRightLeft className="w-3 h-3" />
                                      </Button>
                                    )}
                                    <Button variant="ghost" size="sm" className="h-7 text-[10px] text-amber-600"
                                      onClick={() => { setDeactivateTarget(u); setShowDeactivateDialog(true); }}>
                                      <Archive className="w-3 h-3" />
                                    </Button>
                                  </>
                                )}
                                {tab === "archived" && (
                                  <>
                                    <Button variant="ghost" size="sm" className="h-7 text-[10px] text-green-600" onClick={() => handleRestoreUnit(u.id)}>
                                      <RotateCcw className="w-3 h-3 mr-1" /><span className="hidden sm:inline">{t("restore")}</span>
                                    </Button>
                                    <Button variant="ghost" size="sm" className="h-7 text-[10px] text-red-500" onClick={() => handleTrashUnit(u.id)}>
                                      <Trash2 className="w-3 h-3" />
                                    </Button>
                                  </>
                                )}
                                {tab === "trashed" && (
                                  <>
                                    <Button variant="ghost" size="sm" className="h-7 text-[10px] text-amber-600" onClick={() => handleUntrashUnit(u.id)}>
                                      <RotateCcw className="w-3 h-3 mr-1" /><span className="hidden sm:inline">{t("restoreFromTrash")}</span>
                                    </Button>
                                    <Button variant="ghost" size="sm" className="h-7 text-[10px] text-red-600"
                                      onClick={() => { setPermanentDeleteTarget(u); setShowPermanentDeleteDialog(true); setPermanentDeleteConfirm(""); }}>
                                      <Trash2 className="w-3 h-3" />
                                    </Button>
                                  </>
                                )}
                              </div>
                            </td>
                          </tr>
                        ))}</tbody>
                      </table>
                    )}
                  </TabsContent>
                ))}
              </Tabs>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ═══════════ Users Tab ═══════════ */}
        <TabsContent value="users" className="mt-6">
          <Card className="border border-slate-200 shadow-sm">
            <CardHeader className="flex flex-row items-center justify-between py-4 px-6 border-b border-slate-100">
              <CardTitle className="text-base font-semibold text-slate-800">{t("systemUsers")}</CardTitle>
              <div className="flex items-center gap-2">
                <Dialog open={showBulkDialog} onOpenChange={setShowBulkDialog}>
                  <DialogTrigger asChild><Button variant="outline" size="sm" className="h-9"><Wand2 className="w-4 h-4 mr-1" /> {t("bulkProvision")}</Button></DialogTrigger>
                  <DialogContent>
                    <DialogHeader><DialogTitle>{t("bulkProvisionTitle")}</DialogTitle></DialogHeader>
                    <div className="space-y-4 pt-4">
                      <p className="text-sm text-muted-foreground">{t("bulkProvisionDesc")} <code className="bg-slate-100 px-1 rounded">unit2025</code></p>
                      <p className="text-sm">{t("unitsWithoutUsers")}: <strong>{activeUnits.filter(u => !users.some(usr => usr.unit_id === u.id && usr.role === "UNIT_USER")).length}</strong></p>
                      <Button onClick={handleBulkProvision} className="w-full bg-[#14532d] hover:bg-[#166534] text-white"><Wand2 className="w-4 h-4 mr-2" /> {t("createAll")}</Button>
                    </div>
                  </DialogContent>
                </Dialog>
                <Dialog open={showUserDialog} onOpenChange={setShowUserDialog}>
                  <DialogTrigger asChild><Button data-testid="add-user-button" size="sm" className="h-9 bg-[#14532d] hover:bg-[#166534] text-white"><Plus className="w-4 h-4 mr-1" /> {t("addUser")}</Button></DialogTrigger>
                  <DialogContent>
                    <DialogHeader><DialogTitle>{t("addNewUser")}</DialogTitle></DialogHeader>
                    <div className="space-y-4 pt-4">
                      <div><Label>{t("name")}</Label><Input value={newUser.name} onChange={(e) => setNewUser({...newUser, name: e.target.value})} /></div>
                      <div><Label>{t("email")}</Label><Input type="email" value={newUser.email} onChange={(e) => setNewUser({...newUser, email: e.target.value})} /></div>
                      <div><Label>{t("password")}</Label><Input type="password" value={newUser.password} onChange={(e) => setNewUser({...newUser, password: e.target.value})} /></div>
                      <div><Label>{t("role")}</Label>
                        <Select value={newUser.role} onValueChange={(v) => setNewUser({...newUser, role: v})}>
                          <SelectTrigger><SelectValue /></SelectTrigger>
                          <SelectContent><SelectItem value="CENTRAL_ADMIN">{t("centralAdmin")}</SelectItem><SelectItem value="UNIT_USER">{t("unitUser")}</SelectItem></SelectContent>
                        </Select>
                      </div>
                      {newUser.role === "UNIT_USER" && (
                        <div><Label>{t("assignedUnit")}</Label>
                          <Select value={newUser.unit_id} onValueChange={(v) => setNewUser({...newUser, unit_id: v})}>
                            <SelectTrigger><SelectValue placeholder={t("selectUnit")} /></SelectTrigger>
                            <SelectContent>{activeUnits.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}</SelectContent>
                          </Select>
                        </div>
                      )}
                      <Button onClick={handleCreateUser} className="w-full bg-[#14532d] hover:bg-[#166534] text-white"><Plus className="w-4 h-4 mr-2" /> {t("create")}</Button>
                    </div>
                  </DialogContent>
                </Dialog>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              <table className="w-full text-sm">
                <thead><tr className="border-b border-slate-100 bg-slate-50/50">
                  <th className="text-left py-3 px-6 font-medium text-slate-500 text-xs uppercase tracking-wider">{t("name")}</th>
                  <th className="text-left py-3 px-3 font-medium text-slate-500 text-xs uppercase tracking-wider">{t("email")}</th>
                  <th className="text-left py-3 px-3 font-medium text-slate-500 text-xs uppercase tracking-wider">{t("role")}</th>
                  <th className="text-left py-3 px-3 font-medium text-slate-500 text-xs uppercase tracking-wider">{t("unit")}</th>
                  <th className="text-center py-3 px-3 font-medium text-slate-500 text-xs uppercase tracking-wider">{t("status")}</th>
                  <th className="text-right py-3 px-6 font-medium text-slate-500 text-xs uppercase tracking-wider">{t("actions")}</th>
                </tr></thead>
                <tbody>{users.map((u) => (
                  <tr key={u.id} data-testid={`user-row-${u.id}`} className="border-b border-slate-50 hover:bg-slate-50/50">
                    <td className="py-3 px-6 font-medium text-slate-800">{u.name || "-"}</td>
                    <td className="py-3 px-3 text-slate-600 text-xs">{u.email}</td>
                    <td className="py-3 px-3">
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium ${u.role==="CENTRAL_ADMIN"?"bg-purple-50 text-purple-700":"bg-slate-100 text-slate-600"}`}>
                        <Shield className="w-3 h-3" />{u.role==="CENTRAL_ADMIN"? t("centralAdmin") : t("unitUser")}
                      </span>
                    </td>
                    <td className="py-3 px-3 text-xs">
                      {u.role === "UNIT_USER" ? (
                        <Select defaultValue={u.unit_id || ""} onValueChange={(v) => handleReassignUser(u.id, v)}>
                          <SelectTrigger className="h-7 text-[10px] w-[140px]"><SelectValue placeholder="-" /></SelectTrigger>
                          <SelectContent>{activeUnits.map(un => <SelectItem key={un.id} value={un.id}>{un.name}</SelectItem>)}</SelectContent>
                        </Select>
                      ) : "-"}
                    </td>
                    <td className="py-3 px-3 text-center">{u.active !== false ? <span className="text-green-600 text-xs">{t("active")}</span> : <span className="text-red-500 text-xs">{t("disabled")}</span>}</td>
                    <td className="py-3 px-6 text-right">
                      <div className="flex items-center justify-end gap-1">
                        <Button variant="ghost" size="sm" onClick={() => handleResetPassword(u.id)} className="h-7 text-[10px] text-slate-500"><Key className="w-3 h-3" /></Button>
                        <Button variant="ghost" size="sm" onClick={() => handleToggleActive(u.id, u.active !== false)} className="h-7 text-[10px]">
                          {u.active !== false ? <UserX className="w-3 h-3 text-amber-500" /> : <UserCheck className="w-3 h-3 text-green-500" />}
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => handleDeleteUser(u.id)} className="h-7 text-[10px] text-red-500 hover:bg-red-50"><Trash2 className="w-3.5 h-3.5" /></Button>
                      </div>
                    </td>
                  </tr>
                ))}</tbody>
              </table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ═══════════ Formula Check Tab ═══════════ */}
        <TabsContent value="formulas" className="mt-6">
          <Card className="border border-slate-200 shadow-sm">
            <CardHeader className="py-4 px-6 border-b border-slate-100">
              <CardTitle className="text-base font-semibold text-slate-800 flex items-center gap-2">
                <ShieldCheck className="w-4 h-4 text-[#14532d]" /> {t("formulaCheck")}
              </CardTitle>
              <p className="text-xs text-muted-foreground mt-1">{t("formulaCheckDesc")}</p>
            </CardHeader>
            <CardContent className="p-6 space-y-4">
              <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4">
                <h3 className="text-sm font-semibold text-[#14532d] mb-2">{t("supportedFormulas")}</h3>
                <p className="text-xs text-slate-600">{t("supportedList")}</p>
                <p className="text-xs text-slate-500 mt-1">+ Аритметика (+, -, *, /), Сравнения, Кръстосани референции между листове</p>
              </div>

              <div className="flex items-center gap-3">
                <Select value={formulaScanTemplateId || ""} onValueChange={(v) => { setFormulaScanTemplateId(v); setValidationReport(null); }}>
                  <SelectTrigger data-testid="formula-scan-template" className="w-64"><SelectValue placeholder={t("chooseTemplate")} /></SelectTrigger>
                  <SelectContent>{templates.filter(tp => (tp.deletion_status || "active") === "active").map(tp => <SelectItem key={tp.id} value={tp.id}>{tp.name}</SelectItem>)}</SelectContent>
                </Select>
                {formulaScanTemplateId && (
                  <Button data-testid="run-formula-validation" onClick={() => runFormulaValidation(formulaScanTemplateId)}
                    disabled={validating} className="bg-[#14532d] hover:bg-[#166534] text-white">
                    {validating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <ShieldCheck className="w-4 h-4 mr-2" />}
                    {validating ? t("validationRunning") : t("runValidation")}
                  </Button>
                )}
              </div>

              {validationReport && (
                <div className="space-y-4">
                  {/* Overall Status */}
                  <div className={`flex items-center gap-3 p-4 rounded-lg border ${
                    validationReport.overall_status === "PASS" ? "bg-green-50 border-green-200" : "bg-red-50 border-red-200"
                  }`}>
                    {validationReport.overall_status === "PASS"
                      ? <ShieldCheck className="w-6 h-6 text-green-600" />
                      : <ShieldAlert className="w-6 h-6 text-red-600" />
                    }
                    <div>
                      <p className={`font-semibold ${validationReport.overall_status === "PASS" ? "text-green-700" : "text-red-700"}`}>
                        {validationReport.overall_status === "PASS" ? t("validationPassed") : t("validationFailed")}
                      </p>
                      <p className="text-xs text-slate-500">{t("lastValidation")}: {new Date(validationReport.validated_at).toLocaleString("bg-BG")}</p>
                    </div>
                  </div>

                  {/* Metrics Grid */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="bg-slate-50 rounded-lg p-4"><p className="text-xs text-slate-500 uppercase">{t("totalFormulas")}</p><p className="text-2xl font-semibold text-slate-800">{validationReport.total_formulas}</p></div>
                    <div className="bg-green-50 rounded-lg p-4"><p className="text-xs text-green-600 uppercase">{t("evaluable")}</p><p className="text-2xl font-semibold text-green-700">{validationReport.eval_pass}</p></div>
                    <div className="bg-red-50 rounded-lg p-4"><p className="text-xs text-red-500 uppercase">{t("errors")}</p><p className="text-2xl font-semibold text-red-700">{validationReport.eval_fail}</p></div>
                    <div className="bg-blue-50 rounded-lg p-4">
                      <p className="text-xs text-blue-500 uppercase">{t("goldenCells")}</p>
                      <p className="text-2xl font-semibold text-blue-700">{validationReport.golden_cell_report?.passed}/{validationReport.golden_cell_report?.total}</p>
                    </div>
                  </div>

                  {/* External & Broken Refs */}
                  <div className="grid grid-cols-2 gap-4">
                    <div className="bg-amber-50 border border-amber-100 rounded-lg p-4">
                      <p className="text-xs text-amber-600 uppercase font-medium">{t("externalRefs")}</p>
                      <p className="text-2xl font-semibold text-amber-700">{validationReport.external_ref_count}</p>
                    </div>
                    <div className="bg-red-50 border border-red-100 rounded-lg p-4">
                      <p className="text-xs text-red-500 uppercase font-medium">{t("brokenRefs")}</p>
                      <p className="text-2xl font-semibold text-red-700">{validationReport.broken_ref_count}</p>
                    </div>
                  </div>

                  {/* Formula Types */}
                  {Object.keys(validationReport.type_counts || {}).length > 0 && (
                    <div className="bg-slate-50 rounded-lg p-4">
                      <h4 className="text-xs font-semibold text-slate-600 uppercase mb-2">{t("formulaTypes")}</h4>
                      <div className="flex flex-wrap gap-2">
                        {Object.entries(validationReport.type_counts).sort((a,b) => b[1] - a[1]).map(([ftype, cnt]) => (
                          <span key={ftype} className="px-2 py-1 rounded text-xs font-mono bg-slate-200 text-slate-700">{ftype}: {cnt}</span>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Golden Cell Report */}
                  {validationReport.golden_cell_report?.cells?.length > 0 && (
                    <div className="border border-slate-100 rounded-lg p-4">
                      <h4 className="text-xs font-semibold text-slate-600 uppercase mb-2 flex items-center gap-1">
                        <CheckCircle2 className="w-3.5 h-3.5" /> {t("goldenCells")} ({validationReport.golden_cell_report.passed} {t("goldenCellsPassed")}, {validationReport.golden_cell_report.failed} {t("goldenCellsFailed")})
                      </h4>
                      <div className="max-h-[250px] overflow-y-auto space-y-1">
                        {validationReport.golden_cell_report.cells.map((gc, i) => (
                          <div key={i} className={`flex items-center justify-between px-3 py-1.5 text-xs rounded-md border ${
                            gc.status === "PASS" ? "bg-green-50/50 border-green-100" : "bg-red-50/50 border-red-100"
                          }`}>
                            <span className="font-mono text-[#14532d]">{gc.sheet_name}!{gc.address}</span>
                            <span className={`font-medium ${gc.status === "PASS" ? "text-green-600" : "text-red-600"}`}>{gc.status}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Issues List */}
                  {validationReport.issues?.length > 0 && (
                    <div className="border border-red-100 rounded-lg p-4">
                      <h4 className="text-xs font-semibold text-red-600 uppercase mb-2 flex items-center gap-1">
                        <AlertTriangle className="w-3.5 h-3.5" /> {t("formulaIssuesDesc")} ({validationReport.issue_count})
                      </h4>
                      <div className="max-h-[300px] overflow-y-auto space-y-1.5">
                        {validationReport.issues.map((fc, i) => (
                          <div key={i} className="flex items-start justify-between bg-white rounded-md border border-slate-100 px-3 py-2 text-xs">
                            <div>
                              <span className="font-mono text-[#14532d]">{fc.sheet_name}!{fc.address}</span>
                              <span className="text-slate-400 ml-2 font-mono">{fc.formula?.substring(0, 60)}</span>
                            </div>
                            <span className="text-red-500 text-[10px] ml-2 shrink-0">{fc.value}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {validationReport.eval_fail === 0 && (
                    <div className="flex items-center gap-2 text-green-600 text-sm"><CheckCircle2 className="w-4 h-4" /> {t("noIssues")}</div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* ═══════════ Input Map JSON Tab ═══════════ */}
        <TabsContent value="inputmap" className="mt-6">
          <Card className="border border-slate-200 shadow-sm">
            <CardHeader className="py-4 px-6 border-b border-slate-100">
              <CardTitle className="text-base font-semibold text-slate-800">{t("inputMap")}</CardTitle>
            </CardHeader>
            <CardContent className="p-6 space-y-4">
              <Select value={inputMapTemplateId || ""} onValueChange={(v) => loadInputMap(v)}>
                <SelectTrigger className="mt-1"><SelectValue placeholder={t("chooseTemplate")} /></SelectTrigger>
                <SelectContent>{templates.map((tp) => <SelectItem key={tp.id} value={tp.id}>{tp.name}</SelectItem>)}</SelectContent>
              </Select>
              {inputMapTemplateId && (
                <>
                  <textarea className="w-full h-[400px] mt-1 p-3 text-xs font-mono bg-slate-50 border border-slate-200 rounded-md resize-y"
                    value={inputMapJson} onChange={(e) => setInputMapJson(e.target.value)} />
                  {inputMapError && <p className="text-xs text-red-500 mt-1">{inputMapError}</p>}
                  <Button onClick={saveInputMap} className="bg-[#14532d] hover:bg-[#166534] text-white"><Save className="w-4 h-4 mr-2" /> {t("saveInputMap")}</Button>
                </>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* ═══ Dialogs ═══ */}
      <Dialog open={showEditUnitDialog} onOpenChange={setShowEditUnitDialog}>
        <DialogContent>
          <DialogHeader><DialogTitle>{t("editUnit")}</DialogTitle></DialogHeader>
          {editUnit && (
            <div className="space-y-4 pt-4">
              <div><Label>{t("unitName")}</Label><Input value={editUnit.name} onChange={(e) => setEditUnit({...editUnit, name: e.target.value})} /></div>
              <div><Label>{t("unitCode")}</Label><Input value={editUnit.code} onChange={(e) => setEditUnit({...editUnit, code: e.target.value})} /></div>
              <div><Label>{t("director")}</Label><Input value={editUnit.director || ""} onChange={(e) => setEditUnit({...editUnit, director: e.target.value})} placeholder={t("directorPlaceholder")} /></div>
              <div><Label>{t("accountant")}</Label><Input value={editUnit.accountant || ""} onChange={(e) => setEditUnit({...editUnit, accountant: e.target.value})} placeholder={t("accountantPlaceholder")} /></div>
              <Button onClick={handleEditUnit} className="w-full bg-[#14532d] hover:bg-[#166534] text-white"><Save className="w-4 h-4 mr-2" /> {t("save")}</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={showDeactivateDialog} onOpenChange={setShowDeactivateDialog}>
        <DialogContent>
          <DialogHeader><DialogTitle className="flex items-center gap-2"><AlertTriangle className="w-5 h-5 text-amber-500" />{t("archive")}</DialogTitle></DialogHeader>
          <div className="space-y-4 pt-2">
            <p className="text-sm text-slate-700">{t("deactivateUnitConfirm")}</p>
            {deactivateTarget && <p className="text-sm"><strong>{deactivateTarget.name}</strong> ({deactivateTarget.code})</p>}
            <div className="flex gap-3">
              <Button variant="outline" className="flex-1" onClick={() => setShowDeactivateDialog(false)}>{t("close")}</Button>
              <Button className="flex-1 bg-amber-600 hover:bg-amber-700 text-white" onClick={handleDeactivateUnit}>
                <Archive className="w-4 h-4 mr-2" />{t("archive")}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={showReassignDialog} onOpenChange={setShowReassignDialog}>
        <DialogContent>
          <DialogHeader><DialogTitle className="flex items-center gap-2"><ArrowRightLeft className="w-5 h-5 text-blue-500" />{t("reassignUsers")}</DialogTitle></DialogHeader>
          <div className="space-y-4 pt-2">
            <p className="text-sm text-slate-700">{t("reassignUsersDesc")}</p>
            <Select value={reassignTargetUnit} onValueChange={setReassignTargetUnit}>
              <SelectTrigger><SelectValue placeholder={t("targetUnit")} /></SelectTrigger>
              <SelectContent>{activeUnits.filter(u => u.id !== reassignTarget?.id).map(u => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}</SelectContent>
            </Select>
            <Button disabled={!reassignTargetUnit} onClick={handleReassignUsers} className="w-full bg-blue-600 hover:bg-blue-700 text-white">
              <ArrowRightLeft className="w-4 h-4 mr-2" />{t("reassignUsers")}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Permanent Delete Dialog (Units) */}
      <Dialog open={showPermanentDeleteDialog} onOpenChange={(open) => { setShowPermanentDeleteDialog(open); if (!open) setPermanentDeleteConfirm(""); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="w-5 h-5" />{t("permanentDeleteConfirm")}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <p className="text-sm text-slate-700">{t("permanentDeleteWarning")}</p>
            {permanentDeleteTarget && <p className="text-sm font-medium">{permanentDeleteTarget.name} ({permanentDeleteTarget.code})</p>}
            <div>
              <Label className="text-xs text-red-600">{t("typeDeleteConfirm")}</Label>
              <Input data-testid="permanent-delete-unit-confirm" value={permanentDeleteConfirm}
                onChange={(e) => setPermanentDeleteConfirm(e.target.value)}
                placeholder="DELETE" className="mt-1 border-red-200 focus:ring-red-500" />
            </div>
            <div className="flex gap-3">
              <Button variant="outline" className="flex-1" onClick={() => setShowPermanentDeleteDialog(false)}>{t("close")}</Button>
              <Button data-testid="confirm-permanent-delete-unit" disabled={permanentDeleteConfirm !== "DELETE"}
                className="flex-1 bg-red-600 hover:bg-red-700 text-white" onClick={handlePermanentDeleteUnit}>
                <Trash2 className="w-4 h-4 mr-2" />{t("permanentDelete")}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
